// The app shell's menus (brief 1A-5): which sections and screens a person can open. Pure: no
// database. The items, titles and icons come from docs/design/mockup.html; each screen says what
// it needs from the 1A-4 permissions. A screen someone can't open is in no menu, Ctrl K doesn't
// find it, and its address answers "not available".

import { isAlwaysOn, type Module } from "./modules";
import { allows, allowsSwitch, type Access, type Area, type Switch } from "./permissions";

/**
 * What a screen needs: an area at view (or more), Admin, or a switch. `all`: the area for every
 * record, not "only their own" (a list of every patient, brief 1C-1).
 */
export type ScreenNeed =
  { area: Area; level?: "edit"; all?: true } | { admin: true } | { switch: Switch };

export interface ScreenDef {
  /** The last part of its address, e.g. "billing" in /staffing/billing. */
  id: string;
  title: string;
  /** Octicon name, as in the mockup. */
  icon: string;
  /** Omitted for a section's Overview: it opens when any other screen of the section does. */
  need?: ScreenNeed;
  /** A company feature the screen needs on (brief 1B-5: the shared inbox). */
  feature?: Feature;
  /** A module the screen needs on, in a section that has none (Settings, brief 1C-1). */
  module?: Module;
}

/** Company features that are a switch of their own, not a module (Settings › Modules). */
export type Feature = "shared_inbox";

export interface SectionDef {
  /** The first part of its address. */
  id: string;
  name: string;
  /** The small line over the left menu and page titles (mockup's eyebrow). */
  brand: string;
  icon: string;
  /** The module switch the section belongs to; Settings belongs to none. */
  module?: Module;
  /** Shown in the top menu. Settings is opened from your menu instead. */
  top: boolean;
  /** Hover tip on the top menu button (mockup: Business and Compliance). */
  tip?: string;
  /** Plain words for the section, on Home and in Settings › Modules (mockup). */
  blurb: string;
  screens: readonly ScreenDef[];
}

/** A screen of a section (exported for the manifests, brief 1E-8). */
export const screen = (
  id: string,
  title: string,
  icon: string,
  need?: ScreenNeed,
  feature?: Feature,
): ScreenDef => ({
  id,
  title,
  icon,
  ...(need ? { need } : {}),
  ...(feature ? { feature } : {}),
});
/** The shared inbox's own screens: gone while the company has it off (brief 1B-5). */
export const teamScreen = (id: string, title: string, icon: string, need?: ScreenNeed) =>
  screen(id, title, icon, need, "shared_inbox");
export const areaNeed = (a: Area): ScreenNeed => ({ area: a });

/**
 * Every section, in top menu order, then Settings. Left-menu items as in the mockup. They come
 * from the installed manifests (brief 1E-8: server/core/manifest.ts sets them at start-up).
 */
export let SECTIONS: readonly SectionDef[] = [];

/** Sets the sections from the installed manifests (server/core/manifest.ts installManifests). */
export function useSections(sections: readonly SectionDef[]): void {
  SECTIONS = sections;
}

/**
 * FlowCare Platform (brief 1E-6, mockup v59): FlowCare's own console, from your menu ("FlowCare
 * Platform (staff)"), for FlowCare staff alone. Not a company's section: no module, no role.
 */
export const PLATFORM_SECTION: MenuSection = {
  id: "platform",
  name: "FlowCare Platform",
  brand: "FlowCare Platform",
  icon: "apps",
  top: false,
  blurb: "Every company using FlowCare, and the test companies.",
  screens: [{ id: "tenants", title: "Companies", icon: "organization" }],
};

/** Whether a person's access meets what a screen needs. */
export function meets(access: Access, need: ScreenNeed): boolean {
  if ("admin" in need) return access.admin;
  if ("switch" in need) return allowsSwitch(access, need.switch);
  if (need.all) return access.areas[need.area][need.level ?? "view"] === "all";
  return allows(access, need.area, need.level ?? "view");
}

/** A section as one person sees it: only the screens they can open. */
export interface MenuSection {
  id: string;
  name: string;
  brand: string;
  icon: string;
  top: boolean;
  tip?: string;
  blurb: string;
  screens: { id: string; title: string; icon: string }[];
}

/**
 * The menus for one person: each section whose module is on (Business always is; Settings has
 * no module) with the screens their roles reach. A section with nothing they can open is left out, Overview included.
 */
export function menusFor(
  access: Access,
  modulesOn: readonly string[],
  featuresOff: readonly Feature[] = [],
  staff = false,
): MenuSection[] {
  const out: MenuSection[] = [];
  for (const sec of SECTIONS) {
    if (sec.module && !isAlwaysOn(sec.module) && !modulesOn.includes(sec.module)) continue;
    const offered = sec.screens.filter(
      (x) =>
        (!x.feature || !featuresOff.includes(x.feature)) &&
        (!x.module || isAlwaysOn(x.module) || modulesOn.includes(x.module)),
    );
    const reached = offered.filter((x) => x.need && meets(access, x.need));
    if (reached.length === 0) continue;
    const screens = offered
      .filter((x) => !x.need || reached.includes(x))
      .map(({ id, title, icon }) => ({ id, title, icon }));
    const { id, name, brand, icon, top, tip, blurb } = sec;
    out.push({ id, name, brand, icon, top, ...(tip ? { tip } : {}), blurb, screens });
  }
  if (staff) out.push(PLATFORM_SECTION);
  return out;
}

/** The screen at an address (/section/screen) when this person can open it, otherwise null. */
export function screenAt(
  access: Access,
  modulesOn: readonly string[],
  sectionId: string,
  screenId: string,
  featuresOff: readonly Feature[] = [],
  staff = false,
): { section: MenuSection; screen: { id: string; title: string; icon: string } } | null {
  const section = menusFor(access, modulesOn, featuresOff, staff).find((m) => m.id === sectionId);
  const screen = section?.screens.find((x) => x.id === screenId);
  return section && screen ? { section, screen } : null;
}

/** Everything Ctrl K can find for this person: Home, then every screen they can open. */
export function searchableScreens(
  access: Access,
  modulesOn: readonly string[],
): { path: string; title: string; section: string; icon: string }[] {
  return [
    { path: "/", title: "Home", section: "", icon: "home" },
    ...menusFor(access, modulesOn).flatMap((m) =>
      m.screens.map((x) => ({
        path: `/${m.id}/${x.id}`,
        title: x.title,
        section: m.name,
        icon: x.icon,
      })),
    ),
  ];
}

/** Ctrl K's match: every word typed appears in the screen's title or section, any case. */
export function findScreens<T extends { title: string; section: string }>(
  screens: readonly T[],
  query: string,
): T[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...screens];
  return screens.filter((x) => {
    const text = `${x.title} ${x.section}`.toLowerCase();
    return words.every((w) => text.includes(w));
  });
}
